import {
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Input from '@src/components/common/Input';
import React, { useState } from 'react';
import styled from 'styled-components';

interface SpeakerDraftFormProps {
  /** "Edit Speaker" over a name the transcript read, "New Speaker" from blank. */
  heading: string;
  initialName: string;
  initialTitle: string;
  /** Labels the fields for one row: "Speaker 2". */
  rowLabel: string;
  onSave: (name: string, title: string) => void;
  /** Back to the list, the row as it was. */
  onCancel: () => void;
}

/**
 * The name and title a "New" speaker will be created with, edited in place in
 * the row's mapping menu, so what the transcript or a lower third read can be
 * corrected before Apply rather than in the Speakers panel afterwards.
 */
const SpeakerDraftForm: React.FC<SpeakerDraftFormProps> = ({
  heading,
  initialName,
  initialTitle,
  rowLabel,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(initialName);
  const [title, setTitle] = useState(initialTitle);

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        if (name.trim()) onSave(name.trim(), title.trim());
      }}
      onKeyDown={e => {
        if (e.key !== 'Escape') return;
        // Escape leaves the form, not the menu or the modal behind it.
        e.stopPropagation();
        onCancel();
      }}
    >
      <Heading>{heading}</Heading>
      <Input
        autoFocus
        fullWidth
        label="Name"
        aria-label={`Name for ${rowLabel}`}
        placeholder="Full name"
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <Input
        fullWidth
        label="Title"
        aria-label={`Title for ${rowLabel}`}
        placeholder="Company | Role"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />
      <Actions>
        <SmallSecondaryButton type="button" onClick={onCancel}>
          Cancel
        </SmallSecondaryButton>
        <SmallPrimaryButton type="submit" disabled={!name.trim()}>
          Save
        </SmallPrimaryButton>
      </Actions>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
`;

const Heading = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
`;

const SmallSecondaryButton = styled(SecondaryButton)`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
`;

const SmallPrimaryButton = styled(PrimaryButton)`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
`;

export default SpeakerDraftForm;
